import {
  getNamedType,
  GraphQLNamedType,
  GraphQLSchema,
  isCompositeType,
  SelectionSetNode,
} from 'query-api-graphql';
import React, {ReactNode, useCallback, useState} from 'react';

// Packaging note: We will need to figure out how to do assertions such that error messages are sent to the
// right place.
// There are two classes of errors:
// - Errors that are caused by the caller (e.g. they pass a rootField prop that is not a composite type)
// - Errors that are caused by a bug in this component (e.g. trying to access a field that does not exist on
//   a GraphQLObjectType)
// One option is to send the former to the monolith Sentry and the latter to a package- or ui-library-
// specific Sentry. Either way we'll need a standardized way to differentiate.
import {assert} from 'Asserts';

import CompositeField from './CompositeField';
import {GraphQLFieldSelectorContext, GraphQLFieldSelectorContextOutput} from './GraphQLFieldSelectorContext';
import {GraphQLField} from './GraphQLFieldSelectorTypes';

/**
 * Number of indentation levels in the tree that should be expanded by default.
 *
 * Eventually, this could be a prop to `GraphQLFieldSelector`.
 * Or it could be determined by the caller if/when we make expansion state controlled.
 *
 * For now, just hardcode it for simplicity.
 *
 * @package
 * */
export const DEFAULT_INITIALLY_EXPANDED_LEVELS = 1;

function defaultRenderFieldName(field: GraphQLField): ReactNode {
  return field.name;
}

function defaultRenderTypeName(type: GraphQLNamedType): ReactNode {
  return type.name;
}

function defaultGetIsTypeDisabled(type: GraphQLNamedType): boolean {
  return false;
}

function defaultGetSortedFilteredFields(fields: Array<GraphQLField>): Array<GraphQLField> {
  return fields.slice().sort((a, b) => a.name.localeCompare(b.name));
}

export interface GraphQLFieldSelectorProps {
  /**
   * Limits the depth that can be drilled down to in the selector. Logical depth is incremented for each
   * expansion of object fields, but is not incremented when a possible type of a union or interface is
   * expanded.
   *
   * @example
   * For selections representing the following query string:
   * ```
   * entity {
   *   id # logicalDepth = 1
   *   owner {
   *     id # logicalDepth = 2
   *     ... on User {
   *       name # logicalDepth = 2
   *       org {
   *         id # logicalDepth = 3
   *       }
   *     }
   *   }
   * }
   * ```
   */
  logicalDepthLimit: number;

  /**
   * AST node for the selections that the user has made so far.
   *
   * @see astexplorer.net for examples
   *
   * Restrictions:
   * - Does not (currently) support field arguments
   *   TODO(BNCH-54203): Allow user to hide fields and raise a soft error if non-hidden fields with required
   *   arguments are rendered.
   * - Does not supported aliases
   * - Does not support fragment spreads
   *   ```
   *   x {
   *     ...Hello
   *   }
   *   ```
   * - Does not support nested inline fragments
   *   ```
   *   ... on X {
   *     ... on Y {
   *       id
   *     }
   *   }
   *   ```
   *
   * @example
   * Query string:
   * ```
   * {
   *   id
   *   entry {
   *     id
   *     ... on Org {
   *       handle
   *     }
   *   }
   * }
   * ```
   *
   * corresponds to this `selectionSet` AST node:
   * ```
   * {
   *   kind: 'SelectionSet',
   *   selections: [
   *     {
   *       kind: 'Field',
   *       name: {
   *         kind: 'Name',
   *         value: 'id',
   *       },
   *     {
   *       kind: 'Field',
   *       name: {
   *         kind: 'Name',
   *         value: 'entry',
   *       },
   *       selectionSet: {
   *         kind: 'SelectionSet',
   *         selections: [
   *           {
   *             kind: 'Field',
   *             name: {
   *               kind: 'Name',
   *               value: 'id',
   *             },
   *           },
   *           {
   *             kind: 'InlineFragment',
   *             typeCondition: {
   *               kind: 'NamedType',
   *               name: {kind: 'Name', value: 'Org'}
   *             },
   *             selectionSet: {
   *               kind: 'SelectionSet',
   *               selections: [{
   *                 kind: 'Field',
   *                 name: {kind: 'Name', value: 'handle'},
   *               }]
   *             },
   *           },
   *         ],
   *       },
   *     },
   *   ],
   * }
   * ```
   */
  selectionSet: SelectionSetNode;

  /**
   * Callback to update the selection set. Will only be called with valid selection sets, except when there
   * are no selections, which the caller should handle.
   *
   * Two examples of how this could be handled:
   * - Disallowing such changes altogether:
   *   ```
   *   onChangeSelectionSet={(newSelectionsSet) => {
   *     if (newSelectionSet.selections.length === 0) {return}
   *     ...
   *   }}
   *   ```
   * - Disabling a button or form that submits a query built with `GraphQLFieldSelector`
   *   ```
   *   const [selectionSet, setSelectionSet] = useState<SelectionSetNode>(...);
   *   ...
   *   <Button disabled={selectionSet.selections.length === 0}>Submit</Button>
   *   ```
   */
  onChangeSelectionSet: (newSelectionSet: SelectionSetNode) => void;

  /**
   * Function to sort and filter a list of fields before they're rendered.
   *
   * If the output is empty, `noFieldsDisplayText` will be rendered instead of any fields.
   *
   * @default
   * sort alphabetically, keep all fields
   */
  getSortedFilteredFields?: (fields: Array<GraphQLField>) => Array<GraphQLField>;

  /**
   * The GraphQL schema corresponding to the GraphQL query that is being built.
   *
   * Can be built with `buildClientSchema` from the `graphql` package or using an existing
   * `GraphQLSchema` object.
   */
  graphQLSchema: GraphQLSchema;

  /**
   * Determine whether a type is disabled or not. If it is, fields with that named type will not be selectable
   * or expandable.
   *
   * @default () => false
   */
  getIsTypeDisabled?: (type: GraphQLNamedType) => boolean;

  /**
   * Text to show if there are no fields to show under an expandable field.
   *
   * This should only happen when `getSortedFilteredFields` is defined as a function that can return an
   * empty array.
   *
   * @default "No fields available to be selected"
   */
  noFieldsDisplayText?: string;

  /**
   * The root field to display in the selector. It will start off as expanded.
   *
   * The field's named type must be composite, e.g. (`Object`, `List(Object)`, etc.), not a leaf type
   * (`Scalar`, `Enum`, `List(Scalar)`, etc.).
   *
   */
  rootField: GraphQLField;

  /**
   * Function to render a checkbox. It should fit within a 16x16 box; overflow will be hidden.
   *
   * The checkbox is controlled, so you should specify a `noop` for your checkbox's `onChange`
   * if one is required.
   */
  renderCheckbox: (isChecked: boolean) => JSX.Element;

  /**
   * Function to render an expand/collapse icon for composite fields. This is rendered to the left of the
   * field's type icon. It should fit within a 16x16 box; overflow will be hidden.
   */
  renderExpandIcon: (
    isExpanded: boolean,
    options: {
      /** If the icon is being rendered at or beyond `logicalDepthLimit`, `atMaxDepth` will be `true`. */
      atMaxDepth: boolean;
      /**
       * If the icon is being rendered for a field whose type is disabled as determined by
       * `getIsTypeDisabled`, `isTypeDisabled` will be true.
       */
      isTypeDisabled: boolean;
    }
  ) => JSX.Element;

  /**
   * Function to render a field's name. If the return value is `null`, the field's name will be rendered.
   *
   * @default (field) => field.name
   *
   * @example
   * (field) => isTagSchema(field.name) ? <TagSchemaName id={field.name} /> : null;
   *
   * @example
   * (field) => camelCaseToCapital(field.name) // e.g. "modifiedAt" -> "Modified at"
   *
   */
  renderFieldName?: (field: GraphQLField) => ReactNode;

  /**
   * Function to render a type's name. If the return value is `null`, the type's name will be rendered.
   *
   * @default (type) => type.name
   *
   * @example
   * (type) => `${type.name} type`
   */
  renderTypeName?: (type: GraphQLNamedType) => ReactNode;

  /**
   * Function to render a field's type icon. This is rendered to the left of the field's name.
   * It should fit within a 16x16 box; overflow will be hidden.
   *
   * If not provided, the field's type will not be rendered and the space will be used for the field's name
   * instead.
   *
   * @example
   * (field) => <Icon name={convertGraphQLTypeToIcon(field.type)} />
   */
  renderFieldTypeIcon?: (field: GraphQLField) => JSX.Element;
}

/**
 * Return getters and setters for expansion state based on `stateId` and indentation level.
 *
 * State is elevated to a single `{id -> expansionState}` map so that subfield expansion state is preserved
 * even when a parent field is collapsed then expanded (leading to a dismount/remount of that subfield).
 *
 * TODO(BNCH-54203): We want to persist the expansion state in Redux so expansion state is persisted between
 * `GraphQLFieldSelector` remounts. Since this component is meant to be future-package-proof, we probably
 * don't want to use `makeUseGlobalClientState`/Redux directly from `GraphQLFieldSelector`, but should rather
 * delegate the responsibility of state storage to the caller (who can decide to use `useState`, Redux, or
 * some other storage mechanism as they desire).
 * Two options for doing this:
 * - Add `getIsExpanded` and `toggleIsExpanded` props to `GraphQLFieldSelectorProps`.
 *   Disadvantage: Caller needs to define the logic in `useExpansionState`
 * - Add some sort of `useExpansionState` prop to `GraphQLFieldSelectorProps`, so the caller passes in
 *   `useState` or `useGlobalClientState`.
 *   Disadvantage: Awkward?
 */
function useExpansionState(): Pick<GraphQLFieldSelectorContextOutput, 'getIsExpanded' | 'toggleIsExpanded'> {
  const [expansionState, setExpansionSet] = useState<{[stateId: string]: boolean | undefined}>({});

  const getIsExpanded = useCallback(
    (stateId: string, indentationLevel: number) => {
      const internalState = expansionState[stateId];
      if (internalState === undefined) {
        return indentationLevel < DEFAULT_INITIALLY_EXPANDED_LEVELS;
      } else {
        return internalState;
      }
    },
    [expansionState]
  );

  const toggleIsExpanded = useCallback(
    (stateId: string, indentationLevel: number) => {
      setExpansionSet({...expansionState, [stateId]: !getIsExpanded(stateId, indentationLevel)});
    },
    [expansionState, getIsExpanded]
  );

  return {getIsExpanded, toggleIsExpanded};
}

/**
 * Component to select fields to build up a GraphQL selection set.
 *
 * At least one field must be selected; the user will be prevented from deselecting the last field.
 */
export default function GraphQLFieldSelector({
  getSortedFilteredFields = defaultGetSortedFilteredFields,
  graphQLSchema,
  logicalDepthLimit,
  noFieldsDisplayText = 'No fields available to be selected',
  getIsTypeDisabled = defaultGetIsTypeDisabled,
  renderCheckbox,
  renderExpandIcon,
  renderFieldName = defaultRenderFieldName,
  renderTypeName = defaultRenderTypeName,
  renderFieldTypeIcon,
  rootField,
  selectionSet,
  onChangeSelectionSet,
}: GraphQLFieldSelectorProps): JSX.Element {
  const {getIsExpanded, toggleIsExpanded} = useExpansionState();
  // Unwraps the field's type if it's a List(x) or NonNull(x)
  const namedType = getNamedType(rootField.type);
  assert(isCompositeType(namedType), 'Expected root field to be a composite type', {
    fieldName: rootField.name,
    fieldType: rootField.type.toString(),
  });

  return (
    <GraphQLFieldSelectorContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values -- Disabling for now, but worth fixing
      value={{
        getSortedFilteredFields,
        graphQLSchema,
        logicalDepthLimit,
        noFieldsDisplayText,
        getIsTypeDisabled,
        renderCheckbox,
        renderExpandIcon,
        renderFieldName,
        renderTypeName,
        renderFieldTypeIcon,
        getIsExpanded,
        toggleIsExpanded,
      }}
    >
      <CompositeField
        fieldStateId={rootField.name}
        field={rootField}
        indentationLevel={0}
        logicalDepth={0}
        selectionSet={selectionSet}
        onChangeSelectionSet={onChangeSelectionSet}
      />
    </GraphQLFieldSelectorContext.Provider>
  );
}
